import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { num, ghs } from "../lib/format";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useCountry } from "../context/CountryContext";
import {
  Plus, X, Truck as TruckIcon, GasPump, CheckCircle, PaperPlaneTilt,
  ListBullets, Stack, ArrowRight, FlagBanner, ClipboardText, Package, Coins, PencilSimple,
} from "@phosphor-icons/react";

const STATUS_CHIPS = {
  open:             { label: "Open",        cls: "bg-slate-100 text-slate-700 border-slate-300" },
  trucks_nominated: { label: "Trucks nominated", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  fuel_allocated:   { label: "Fuel allocated",   cls: "bg-sky-50 text-ps-blue border-sky-300" },
  issued:           { label: "Fuel issued",      cls: "bg-lime-100 text-lime-900 border-lime-400" },
  completed:        { label: "Completed",        cls: "bg-emerald-100 text-emerald-900 border-emerald-400" },
  cancelled:        { label: "Cancelled",        cls: "bg-rose-50 text-rose-700 border-rose-300" },
};

const EMPTY_JOB = (country) => ({
  client: "", cargo_description: "", box_count: "",
  origin: "", destination: "", country: country || "GH", notes: "",
});

export default function Evacuations() {
  const { user } = useAuth();
  const { scope, COUNTRIES } = useCountry();
  const isAll = scope === "ALL";
  const country = isAll ? null : scope;

  const [rows, setRows] = useState([]);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // create-job dialog
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(() => EMPTY_JOB(scope === "ALL" ? "GH" : scope));
  const [busy, setBusy] = useState(false);

  // detail drawer
  const [openJobId, setOpenJobId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = country ? { country } : {};
      const [j, c] = await Promise.all([
        api.get("/jobs", { params }),
        api.get("/jobs/fuel-credits", { params }),
      ]);
      setRows(j.data);
      setCredits(c.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country]);

  const totalCredit = useMemo(() => credits.reduce((s, c) => s + (c.credit_l || 0), 0), [credits]);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.client.trim()) { setErr("Client is required"); return; }
    setBusy(true);
    setErr("");
    try {
      const payload = {
        ...form,
        client: form.client.trim().toUpperCase(),
        origin: form.origin.trim().toUpperCase(),
        destination: form.destination.trim().toUpperCase(),
        box_count: form.box_count ? parseInt(form.box_count, 10) : null,
      };
      const { data } = await api.post("/jobs", payload);
      setShowNew(false);
      setForm(EMPTY_JOB(scope === "ALL" ? "GH" : scope));
      await load();
      setOpenJobId(data.id);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (j) => {
    if (!window.confirm(`Delete job ${j.job_number}? Child trips with no fuel issued will also be deleted.`)) return;
    try {
      await api.delete(`/jobs/${j.id}`);
      await load();
    } catch (e) {
      window.alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="evacuations-page">
      <PageHeader
        subtitle={isAll ? "Cargo operations · All countries" : `Cargo operations · ${COUNTRIES[scope].flag} ${COUNTRIES[scope].name}`}
        title="Evacuations"
        action={
          <button onClick={() => { setShowNew(true); setForm(EMPTY_JOB(scope === "ALL" ? "GH" : scope)); }}
            data-testid="new-job-button"
            className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm">
            <Plus size={16} weight="bold" /> New job
          </button>
        }
      />

      {err && (
        <div className="mx-8 mt-3 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md flex items-start justify-between">
          <span>{err}</span><button onClick={() => setErr("")} className="text-red-500"><X size={14} /></button>
        </div>
      )}

      {/* Credits banner */}
      {credits.length > 0 && (
        <div className="px-8 pt-5" data-testid="evac-credits-banner">
          <div className="border border-amber-300 bg-amber-50 rounded-md px-4 py-3 text-sm flex items-start gap-3">
            <Coins size={18} weight="duotone" className="text-amber-700 mt-0.5" />
            <div>
              <div className="font-semibold text-amber-900">{num(totalCredit, 1)} L of leftover fuel available across {credits.length} truck{credits.length === 1 ? "" : "s"}</div>
              <div className="text-xs text-amber-800 mt-0.5">
                {credits.slice(0, 8).map(c => `${c.vehicle_number} (${num(c.credit_l, 0)} L)`).join(" · ")}
                {credits.length > 8 && " · …"}
              </div>
              <div className="text-xs text-amber-700 mt-1 italic">These credits are automatically consumed the next time fuel is issued for that truck.</div>
            </div>
          </div>
        </div>
      )}

      {/* Jobs list */}
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-2">
            <ListBullets size={16} weight="duotone" className="text-ps-blue" />
            <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">All evacuation jobs</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 ml-auto">{rows.length}</div>
          </div>
          {loading ? (
            <div className="p-8"><div className="indus-bar w-1/3" /></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left">Job #</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Client</th>
                  <th className="px-3 py-2 text-left">Cargo</th>
                  <th className="px-3 py-2 text-left">Route</th>
                  <th className="px-3 py-2 text-right">Trucks</th>
                  <th className="px-3 py-2 text-right">Fuel (alloc · issued · used)</th>
                  <th className="px-3 py-2 text-right">Leftover</th>
                  <th className="px-3 py-2 w-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.length === 0 && (
                  <tr><td colSpan={9} className="p-12 text-center text-slate-500">
                    No evacuation jobs yet. Click <span className="font-semibold text-ps-deep">New job</span> to register a cargo move request.
                  </td></tr>
                )}
                {rows.map((j) => {
                  const chip = STATUS_CHIPS[j.status] || STATUS_CHIPS.open;
                  return (
                    <tr key={j.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setOpenJobId(j.id)} data-testid={`job-row-${j.job_number}`}>
                      <td className="px-3 py-2 font-mono-data text-ps-blue font-bold">{j.job_number}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${chip.cls}`}>{chip.label}</span></td>
                      <td className="px-3 py-2 font-semibold text-ps-deep">{j.client}</td>
                      <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[180px]" title={j.cargo_description}>
                        {j.box_count ? <span className="font-semibold text-ps-deep">{j.box_count} </span> : ""}
                        {j.cargo_description || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{j.origin || "?"} → {j.destination || "?"}</td>
                      <td className="px-3 py-2 text-right font-mono-data tabular">{j.nominated_trucks?.length || 0}</td>
                      <td className="px-3 py-2 text-right font-mono-data tabular text-xs">
                        {num(j.fuel_total_allocated_l)} · <span className="text-lime-700">{num(j.fuel_total_issued_l)}</span> · {num(j.fuel_total_used_l)} L
                      </td>
                      <td className="px-3 py-2 text-right font-mono-data tabular">
                        {j.fuel_leftover_l > 0 ? <span className="text-amber-700 font-semibold">{num(j.fuel_leftover_l)} L</span> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {(j.status === "open" || j.status === "trucks_nominated") && (
                          <button onClick={(e) => { e.stopPropagation(); remove(j); }} className="text-slate-400 hover:text-red-500 p-1"><X size={14} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Detail drawer */}
      {openJobId && <JobDrawer jobId={openJobId} onClose={() => { setOpenJobId(null); load(); }} currentUser={user} />}

      {/* Create modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" onClick={() => !busy && setShowNew(false)}>
          <form onSubmit={submitNew} className="bg-white rounded-xl shadow-xl w-full max-w-lg border border-slate-200" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Cargo operations</div>
              <h2 className="font-display font-extrabold text-2xl text-ps-deep tracking-tight">New evacuation job</h2>
              <p className="text-xs text-slate-500 mt-1">Step 1 — register the haul request. You&apos;ll nominate trucks next.</p>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <Field label="Client *" testid="job-client" value={form.client} onChange={(v) => setForm({ ...form, client: v })} />
              <Field label="Boxes / containers" testid="job-boxes" type="number" min="0" value={form.box_count} onChange={(v) => setForm({ ...form, box_count: v })} />
              <div className="col-span-2">
                <Field label="Cargo description" testid="job-cargo" value={form.cargo_description} onChange={(v) => setForm({ ...form, cargo_description: v })} placeholder="e.g. 50x 40HC reefers, Maersk fleet" />
              </div>
              <Field label="Origin" testid="job-origin" value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} placeholder="Tema Port" />
              <Field label="Destination" testid="job-destination" value={form.destination} onChange={(v) => setForm({ ...form, destination: v })} placeholder="Yard B" />
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Country</label>
                <div className="flex gap-2" data-testid="job-country-row">
                  {["GH", "TG", "BJ"].map((k) => (
                    <button type="button" key={k} onClick={() => setForm({ ...form, country: k })}
                      className={`flex-1 px-3 py-2 rounded-md border text-sm font-semibold ${form.country === k ? "bg-ps-blue text-white border-ps-blue" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                      {COUNTRIES[k].flag} {COUNTRIES[k].name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-span-2">
                <Field label="Notes (optional)" testid="job-notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
              </div>
            </div>
            <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 rounded-b-xl">
              <button type="button" onClick={() => setShowNew(false)} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100 rounded">Cancel</button>
              <button type="submit" disabled={busy} data-testid="job-create-submit" className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep rounded disabled:opacity-50">{busy ? "Creating…" : "Create job"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


// ---------- Job detail drawer ----------
function JobDrawer({ jobId, onClose, currentUser }) {
  const [job, setJob] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trucksInput, setTrucksInput] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [allocs, setAllocs] = useState({});  // {trip_id: {fuel_l, litres_per_trip, driver_name}}
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [j, t, d] = await Promise.all([
        api.get(`/jobs/${jobId}`),
        api.get(`/jobs/${jobId}/trips`),
        api.get(`/drivers`).catch(() => ({ data: [] })),
      ]);
      setJob(j.data);
      setTrips(t.data);
      setDrivers(d.data || []);
      // initialise allocations
      const a = {};
      for (const tr of t.data) a[tr.id] = { fuel_l: tr.fuel_approved_l || "", litres_per_trip: tr.litres_per_trip || 10, driver_name: tr.driver_name || "" };
      setAllocs(a);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load job");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const isAdmin = currentUser?.role === "admin";
  const canAllocate = ["admin", "manager", "operator"].includes(currentUser?.role);
  const canIssue = ["admin", "fuel_approver"].includes(currentUser?.role);

  const doNominate = async () => {
    const trucks = trucksInput.split(/[,\n]/).map(t => t.trim().toUpperCase()).filter(Boolean);
    if (!trucks.length) { setErr("Enter at least one truck"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/jobs/${jobId}/nominate-trucks`, { trucks });
      setTrucksInput("");
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doAllocate = async () => {
    const allocations = trips
      .map(t => ({
        trip_id: t.id,
        fuel_l: parseFloat(allocs[t.id]?.fuel_l) || 0,
        litres_per_trip: parseFloat(allocs[t.id]?.litres_per_trip) || 10,
        driver_name: (allocs[t.id]?.driver_name || "").trim() || null,
      }))
      .filter(a => a.fuel_l > 0);
    if (!allocations.length) { setErr("Allocate fuel to at least one truck"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/jobs/${jobId}/allocate`, { allocations });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doIssue = async () => {
    setBusy(true); setErr("");
    try { await api.post(`/jobs/${jobId}/issue`); await load(); }
    catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const doClose = async () => {
    if (!window.confirm("Close this job? Any leftover fuel (planned trips − done trips) × L per trip will be credited to each truck for future jobs.")) return;
    setBusy(true); setErr("");
    try { await api.post(`/jobs/${jobId}/close`); await load(); }
    catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const updateTripsDone = async (trip) => {
    const n = window.prompt(`Trips done for ${trip.vehicle_number} (1–${trip.trips_planned})`, String(trip.trips_done || 1));
    if (!n) return;
    try {
      await api.patch(`/activities/${trip.id}/trips-done`, { trips_done: parseInt(n, 10) });
      await load();
    } catch (e) {
      window.alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex justify-end" onClick={onClose} data-testid="job-drawer">
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto border-l border-slate-200" onClick={(e) => e.stopPropagation()}>
        {loading || !job ? (
          <div className="p-8"><div className="indus-bar w-1/3" /></div>
        ) : (
          <>
            <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Evacuation job</div>
                  <h2 className="font-display font-extrabold text-3xl tracking-tight text-ps-deep">{job.job_number}</h2>
                  <div className="text-sm text-slate-600 mt-1">{job.client} · {job.box_count ? `${job.box_count} boxes · ` : ""}{job.origin} → {job.destination}</div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"><X size={20} /></button>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded border ${STATUS_CHIPS[job.status]?.cls || ""}`}>
                  {STATUS_CHIPS[job.status]?.label || job.status}
                </span>
                <span className="text-slate-500">Created by {job.created_by}</span>
                {job.allocated_by && <span className="text-slate-500">· Allocated by {job.allocated_by}</span>}
                {job.issued_by && <span className="text-slate-500">· Issued by {job.issued_by}</span>}
              </div>
            </div>

            {err && (
              <div className="mx-6 mt-4 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md">{err}</div>
            )}

            {/* Step 1 — Nominate trucks */}
            <Step n={1} title="Nominate trucks" done={["trucks_nominated", "fuel_allocated", "issued", "completed"].includes(job.status)} active={["open", "trucks_nominated"].includes(job.status)}>
              {trips.length > 0 && (
                <div className="space-y-1.5 mb-3" data-testid="nominated-trucks">
                  {trips.map((t) => (
                    <NominatedTruckRow key={t.id} trip={t} drivers={drivers} jobCompleted={job.status === "completed"} onSaved={load} />
                  ))}
                </div>
              )}
              {["open", "trucks_nominated"].includes(job.status) && (
                <div className="flex gap-2">
                  <input
                    value={trucksInput}
                    onChange={(e) => setTrucksInput(e.target.value)}
                    placeholder="GT 9160, AS 2701, …"
                    data-testid="trucks-nominate-input"
                    className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data uppercase"
                  />
                  <button onClick={doNominate} disabled={busy || !trucksInput.trim()} data-testid="trucks-nominate-button"
                    className="bg-ps-blue text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-ps-deep disabled:opacity-50 flex items-center gap-1">
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>
              )}
            </Step>

            {/* Step 2 — Allocate fuel (Seth/Gillion) */}
            <Step n={2} title="Allocate fuel (Seth / Gillion)" done={["fuel_allocated", "issued", "completed"].includes(job.status)} active={job.status === "trucks_nominated" || job.status === "fuel_allocated"}>
              {trips.length === 0 ? (
                <div className="text-xs text-slate-500 italic">Nominate trucks first.</div>
              ) : (
                <>
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                      <tr><th className="text-left pb-2">Truck · Driver</th><th className="text-right pb-2 w-28">Fuel L</th><th className="text-right pb-2 w-28">L/trip</th><th className="text-right pb-2 w-24">→ trips</th></tr>
                    </thead>
                    <tbody>
                      {trips.map((t) => {
                        const a = allocs[t.id] || {};
                        const q = parseFloat(a.fuel_l) || 0;
                        const lpt = parseFloat(a.litres_per_trip) || 10;
                        const planned = q > 0 ? Math.max(1, Math.floor(q / lpt)) : "—";
                        const locked = ["fuel_allocated", "issued", "completed"].includes(job.status);
                        return (
                          <tr key={t.id} className="border-t border-slate-100">
                            <td className="py-2">
                              <div className="font-mono-data font-bold text-ps-blue">{t.vehicle_number}</div>
                              <div className="text-[11px] text-slate-500 font-mono-data">
                                {t.driver_name || <span className="italic text-amber-700">no driver yet</span>}
                              </div>
                            </td>
                            <td className="py-2"><input type="number" min="0" disabled={locked || !canAllocate} value={a.fuel_l} onChange={(e) => setAllocs({ ...allocs, [t.id]: { ...a, fuel_l: e.target.value } })} data-testid={`alloc-fuel-${t.vehicle_number}`} className="w-full border border-slate-300 rounded px-2 py-1 text-right tabular font-mono-data disabled:bg-slate-50 disabled:text-slate-500" /></td>
                            <td className="py-2"><input type="number" min="1" disabled={locked || !canAllocate} value={a.litres_per_trip} onChange={(e) => setAllocs({ ...allocs, [t.id]: { ...a, litres_per_trip: e.target.value } })} className="w-full border border-slate-300 rounded px-2 py-1 text-right tabular font-mono-data disabled:bg-slate-50 disabled:text-slate-500" /></td>
                            <td className="py-2 text-right font-mono-data text-lime-700 font-bold">{planned}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {(job.status === "trucks_nominated" || (job.status === "fuel_allocated" && canAllocate)) && (
                    <div className="mt-3 flex justify-end">
                      {!canAllocate ? (
                        <div className="text-xs text-amber-700 italic">Only Seth / Gillion (manager/operator/admin) can allocate fuel.</div>
                      ) : (
                        <button onClick={doAllocate} disabled={busy} data-testid="allocate-button" className="bg-ps-blue text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-ps-deep disabled:opacity-50 flex items-center gap-2">
                          <ClipboardText size={14} weight="bold" /> Allocate fuel
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </Step>

            {/* Step 3 — Issue fuel (Collins) */}
            <Step n={3} title="Issue fuel (Collins)" done={["issued", "completed"].includes(job.status)} active={job.status === "fuel_allocated"}>
              {job.status === "fuel_allocated" && (
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-600">{num(job.fuel_total_allocated_l)} L allocated across {trips.length} trucks. Confirming will create dispense records, apply any per-truck fuel credits, and reduce yard stock.</div>
                  {canIssue ? (
                    <button onClick={doIssue} disabled={busy} data-testid="issue-button" className="bg-lime-500 text-ps-deep px-4 py-2 rounded-md text-sm font-bold hover:bg-lime-600 disabled:opacity-50 flex items-center gap-2 ml-3 shrink-0">
                      <GasPump size={14} weight="bold" /> Issue fuel
                    </button>
                  ) : (
                    <div className="text-xs text-amber-700 italic ml-3">Only Collins (fuel_approver/admin) can issue fuel.</div>
                  )}
                </div>
              )}
              {["issued", "completed"].includes(job.status) && (
                <div className="text-xs text-slate-600">Issued {num(job.fuel_total_issued_l)} L on {job.issued_at?.slice(0, 10)}.</div>
              )}
            </Step>

            {/* Step 4 — Work + reconcile */}
            <Step n={4} title="Work & reconcile trips" done={job.status === "completed"} active={job.status === "issued"}>
              {trips.length > 0 && ["issued", "completed"].includes(job.status) && (
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                    <tr><th className="text-left pb-2">Truck</th><th className="text-left pb-2">Driver</th><th className="text-right pb-2 w-32">Trips done / planned</th><th className="text-right pb-2 w-24">Used (L)</th><th className="text-right pb-2 w-24">Leftover (L)</th></tr>
                  </thead>
                  <tbody>
                    {trips.map((t) => {
                      const done = t.trips_done || 0;
                      const planned = t.trips_planned || 1;
                      const lpt = t.litres_per_trip || 10;
                      const used = (done * lpt).toFixed(0);
                      const left = Math.max(0, (planned - done) * lpt).toFixed(0);
                      const at_cap = done >= planned;
                      return (
                        <tr key={t.id} className="border-t border-slate-100">
                          <td className="py-2 font-mono-data font-bold text-ps-blue">{t.vehicle_number}</td>
                          <td className="py-2 text-sm text-slate-700">{t.driver_name || <span className="italic text-slate-400">—</span>}</td>
                          <td className="py-2 text-right">
                            <button onClick={() => updateTripsDone(t)} disabled={job.status === "completed"}
                              data-testid={`drawer-trips-${t.vehicle_number}`}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border-2 text-sm font-bold transition-all ${
                                at_cap ? "border-emerald-600 bg-emerald-500 text-white" : "border-lime-500 bg-lime-400 text-ps-deep hover:bg-lime-500"
                              } ${job.status === "completed" ? "opacity-60 cursor-not-allowed" : ""}`}>
                              {done} / {planned} {job.status !== "completed" && <PencilSimple size={11} weight="bold" />}
                            </button>
                          </td>
                          <td className="py-2 text-right font-mono-data tabular">{used}</td>
                          <td className="py-2 text-right font-mono-data tabular">{left > 0 ? <span className="text-amber-700 font-semibold">{left}</span> : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {job.status === "issued" && (
                <div className="mt-3 flex justify-end">
                  <button onClick={doClose} disabled={busy} data-testid="close-button"
                    className="bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                    <FlagBanner size={14} weight="bold" /> Close job
                  </button>
                </div>
              )}
              {job.status === "completed" && (
                <div className="text-xs text-emerald-700">
                  Job closed on {job.closed_at?.slice(0, 10)}. Used <span className="font-bold">{num(job.fuel_total_used_l)} L</span> · <span className="font-bold text-amber-700">{num(job.fuel_leftover_l)} L</span> credited back to trucks.
                </div>
              )}
            </Step>
          </>
        )}
      </div>
    </div>
  );
}

function NominatedTruckRow({ trip, drivers, jobCompleted, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(trip.driver_name || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const v = val.trim().toUpperCase();
    if (v === (trip.driver_name || "")) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/activities/${trip.id}`, { driver_name: v });
      setEditing(false);
      await onSaved?.();
    } catch (e) {
      window.alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-ps-blue bg-sky-50">
      <TruckIcon size={14} weight="bold" className="text-ps-blue shrink-0" />
      <span className="font-mono-data font-bold text-ps-deep">{trip.vehicle_number}</span>
      <span className="text-slate-400">·</span>
      {editing ? (
        <>
          <input
            type="text"
            value={val}
            onChange={(e) => setVal(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            list={`drivers-${trip.id}`}
            placeholder="Driver name"
            data-testid={`driver-edit-input-${trip.vehicle_number}`}
            autoFocus
            disabled={busy}
            className="flex-1 border border-ps-blue rounded px-2 py-1 text-sm font-mono-data uppercase focus:outline-none focus:ring-2 focus:ring-ps-blue/30"
          />
          <datalist id={`drivers-${trip.id}`}>
            {drivers.map((d) => <option key={d.id} value={d.name} />)}
          </datalist>
          <button onClick={save} disabled={busy} data-testid={`driver-save-${trip.vehicle_number}`} className="bg-ps-blue text-white px-3 py-1 rounded text-xs font-bold hover:bg-ps-deep disabled:opacity-50">Save</button>
          <button onClick={() => { setEditing(false); setVal(trip.driver_name || ""); }} className="text-slate-400 hover:text-slate-700 p-1"><X size={14} /></button>
        </>
      ) : (
        <>
          {trip.driver_name ? (
            <span className="font-mono-data font-semibold text-ps-deep flex-1">{trip.driver_name}</span>
          ) : (
            <span className="text-xs text-amber-700 italic font-semibold flex-1">No driver assigned yet</span>
          )}
          {!jobCompleted && (
            <button
              onClick={() => setEditing(true)}
              data-testid={`driver-edit-${trip.vehicle_number}`}
              className="text-xs uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep flex items-center gap-1 px-2 py-1 rounded hover:bg-white"
            >
              <PencilSimple size={12} weight="bold" /> {trip.driver_name ? "Change" : "Add driver"}
            </button>
          )}
        </>
      )}
    </div>
  );
}


function Step({ n, title, done, active, children }) {
  return (
    <div className={`px-6 py-5 border-b border-slate-100 ${active ? "bg-sky-50/30" : ""}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${done ? "bg-emerald-500 text-white" : active ? "bg-ps-blue text-white" : "bg-slate-200 text-slate-500"}`}>
          {done ? <CheckCircle size={14} weight="fill" /> : n}
        </div>
        <h3 className={`font-display font-extrabold text-lg tracking-tight ${active || done ? "text-ps-deep" : "text-slate-400"}`}>{title}</h3>
      </div>
      <div className="ml-10">{children}</div>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, ...rest }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-ps-blue rounded"
        {...rest}
      />
    </div>
  );
}
